import { isDefined } from "~/shared/common";
import { FolderKind, IconKind, PageKind, SpaceKind, SprintMode, UserRole } from "~/shared/enums";
import type { Dartboard, Folder, Space, SpaceUpdate, TransactionResponse } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { deepCopy, filterInPlace, getNextTitleInSequence, makeDuid, randomSample } from "~/utils/common";
import { spaceComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToSpaces: Map<string, Space>;
  _sortedSpaceDuids: string[];
};

export const state: State = {
  _duidsToSpaces: new Map(),
  _sortedSpaceDuids: [],
};

export type Getters = {
  spaceList: Space[];
  getSpaceByDuid: (duid: string) => Space | undefined;
  getSpacesByDuidsOrdered: (duids: string[]) => Space[];
  workspaceSpace: Space | undefined;
  personalSpace: Space;
};

export type Actions = {
  createSpace: (order: string, partialSpace?: Partial<Space>, options?: { awaitBackend?: boolean }) => Promise<Space>;
  updateSpace: (update: SpaceUpdate, options?: { awaitBackend?: boolean }) => Promise<Space | undefined>;
  updateSpaceSprintMode: (
    spaceDuid: string,
    sprintMode: SprintMode,
    options?: { awaitBackend?: boolean }
  ) => Promise<Space | undefined>;
  updateSpaceAccessors: (
    spaceDuid: string,
    accessorDuids: string[],
    add?: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<Space | undefined>;
  replicateSpace: (
    space: Space,
    order: string,
    partialSpace?: Partial<Space>,
    options?: { awaitBackend?: boolean; maintainTitle?: boolean }
  ) => Promise<Space | undefined>;
  deleteSpace: (space: Space, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setSpaces: (spaces: Space[]) => void;
  $createOrUpdateSpace: (space: Space) => void;
  $deleteSpace: (space: Space) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  spaceList() {
    const pageStore = this.$usePageStore();
    const userStore = this.$useUserStore();
    return this._sortedSpaceDuids
      .map((e) => this._duidsToSpaces.get(e))
      .filter(isDefined)
      .filter((e) => userStore.isRoleGte(UserRole.MEMBER) || pageStore.isPublic || !e.accessibleByTeam);
  },
  getSpaceByDuid() {
    return (duid) => this._duidsToSpaces.get(duid);
  },
  getSpacesByDuidsOrdered() {
    return (duids) => this.spaceList.filter((e) => duids.includes(e.duid));
  },
  workspaceSpace() {
    if (!this.$useUserStore().isRoleGte(UserRole.MEMBER)) {
      return undefined;
    }

    const workspace = this.spaceList.filter((e) => e.kind === SpaceKind.WORKSPACE);
    if (workspace.length !== 1) {
      throw new Error("Expected exactly one workspace space");
    }
    return workspace[0];
  },
  personalSpace() {
    const personal = this.spaceList.filter((e) => e.kind === SpaceKind.PERSONAL);
    if (personal.length !== 1) {
      throw new Error("Expected exactly one personal space");
    }
    return personal[0];
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createSpace(order, draftSpace, options = {}) {
    const { awaitBackend = false } = options;
    const appStore = this.$useAppStore();

    const title = getNextTitleInSequence(
      "Space",
      this.spaceList.map((e) => e.title),
      { noCopy: true }
    );
    const abrev = getNextTitleInSequence(
      "SPAC",
      this.spaceList.map((e) => e.abrev),
      { noCopy: true, maxLength: 4, separator: "" }
    );

    const space: Space = {
      pageKind: PageKind.SPACE,
      duid: makeDuid(),
      order,
      title,
      description: "",
      abrev,
      accessibleByTeam: true,
      accessibleByUserDuids: [],
      sprintMode: SprintMode.NONE,
      sprintReplicateOnRollover: false,
      sprintNameFmt: "Sprint {N}",
      kind: SpaceKind.OTHER,
      iconKind: IconKind.NONE,
      iconNameOrEmoji: randomSample(appStore.pageIconOptions)[0].name,
      colorHex: makeRandomColorHex(),
      rolloverRecurrence: null,
      rolloverRecursNextAt: null,
      ...draftSpace,
    };

    this.$createOrUpdateSpace(space);

    const backendAction = this.$backend.space.create(space);
    if (awaitBackend) {
      await backendAction;
    }

    // TODO transactionify and dedupe with similar code in dartboard.ts
    await this.createDartboard(
      space.duid,
      String.fromCharCode(129),
      {
        title: "Tasks",
        iconKind: IconKind.ICON,
        iconNameOrEmoji: "TaskIcon",
        colorHex: "#b49bf8",
      },
      options
    );

    return space;
  },
  async updateSpace(update, options = {}) {
    const { awaitBackend = false } = options;
    const space = this.getSpaceByDuid(update.duid);
    if (!space) {
      return undefined;
    }

    Object.assign(space, update);
    updateInSorted(this._duidsToSpaces, this._sortedSpaceDuids, space.duid, spaceComparator);

    const backendAction = this.$backend.space.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return space;
  },
  async updateSpaceSprintMode(spaceDuid, sprintMode, options = {}) {
    const { awaitBackend = false } = options;
    const space = this.getSpaceByDuid(spaceDuid);
    if (!space) {
      return undefined;
    }

    space.sprintMode = sprintMode;

    const dartboardCreates =
      sprintMode !== SprintMode.NONE ? await this.createSprintDartboardsNoBackend(space.duid) : undefined;
    let layoutCreates;
    let userDartboardLayoutCreates;
    if (dartboardCreates !== undefined) {
      const userDuid = this.$useUserStore().duid;
      layoutCreates = [];
      userDartboardLayoutCreates = [];
      dartboardCreates.forEach((dartboard) => {
        const layout = this.getLayoutByDartboardDuid(dartboard.duid);
        if (!layout) {
          return;
        }
        layoutCreates.push(layout);
        userDartboardLayoutCreates.push({
          userDuid,
          dartboardDuid: dartboard.duid,
          layoutDuid: layout.duid,
        });
      });
    }
    const backendAction = this.$backend.space.updateSprintMode(
      {
        duid: space.duid,
        sprintMode,
      },
      dartboardCreates,
      layoutCreates,
      userDartboardLayoutCreates,
      sprintMode === SprintMode.NONE ? await this.deleteSprintDartboardsNoBackend(space.duid) : undefined
    );
    if (awaitBackend) {
      await backendAction;
    }
    return space;
  },
  async updateSpaceAccessors(duid, accessorDuids, add, options = {}) {
    const { awaitBackend = false } = options;
    const space = this.getSpaceByDuid(duid);
    if (!space) {
      return undefined;
    }

    let backendAction: Promise<TransactionResponse>;

    if (add) {
      space.accessibleByUserDuids.push(...accessorDuids);
      backendAction = this.$backend.space.updateListAdd({
        duid: space.duid,
        accessibleByUserDuids: accessorDuids,
      });
    } else {
      filterInPlace(space.accessibleByUserDuids, (e) => !accessorDuids.includes(e));
      backendAction = this.$backend.space.updateListRemove({
        duid: space.duid,
        accessibleByUserDuids: accessorDuids,
      });
    }

    if (awaitBackend) {
      await backendAction;
    }

    return space;
  },
  async replicateSpace(space, order, partialSpace, options = {}) {
    const { awaitBackend = false, maintainTitle = false } = options;

    const newSpace: Space = {
      ...deepCopy(space),
      duid: makeDuid(),
      kind: SpaceKind.OTHER,
      order,
      ...partialSpace,
    };
    if (!maintainTitle && partialSpace?.title === undefined) {
      newSpace.title = getNextTitleInSequence(
        space.title,
        this.spaceList.map((e) => e.title)
      );
    }

    this.$createOrUpdateSpace(newSpace);

    // TODO transactionify these calls
    const spaceCreate = this.$backend.space.create(newSpace);

    const dartboards = this.getDartboardsBySpaceDuidOrdered(space.duid, { excludeFinished: true });
    const dartboardCreates = Promise.all(
      dartboards.map((dartboard) =>
        this.replicateDartboard(
          dartboard,
          dartboard.order,
          { spaceDuid: newSpace.duid, kind: dartboard.kind },
          { maintainTitle: true }
        )
      )
    );

    const folders = this.getFoldersBySpaceDuidOrdered(space.duid, { includeHidden: true });
    const folderCreates = Promise.all(
      folders.map((folder) =>
        this.replicateFolder(
          folder,
          folder.order,
          { spaceDuid: newSpace.duid, kind: FolderKind.OTHER },
          { maintainTitle: true }
        )
      )
    );

    if (awaitBackend) {
      await Promise.all([spaceCreate, dartboardCreates, folderCreates]);
    }

    return newSpace;
  },
  async deleteSpace(space, options = {}) {
    const { awaitBackend = false } = options;

    const dartboards = this.getDartboardsBySpaceDuidOrdered(space.duid, { includeHidden: true });
    const dartboardDeletions: Promise<void>[] = [];
    dartboards.forEach((dartboard) => {
      dartboardDeletions.push(this.deleteDartboard(dartboard, { noBackend: true }));
    });
    await Promise.all(dartboardDeletions);
    this.$deleteSpace(space);

    const backendAction = this.$backend.space.delete(space.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setSpaces(spaces) {
    setObjectMapAndSorted(
      this._duidsToSpaces,
      this._sortedSpaceDuids,
      spaces.map((e) => ({ ...e, pageKind: PageKind.SPACE }) satisfies Space),
      spaceComparator
    );
  },
  async $createOrUpdateSpace(space) {
    const currentSpace = this.getSpaceByDuid(space.duid);

    // TODO remove this check when we are filtering out spurious updates on BE of WS
    if (
      !(
        (this.$useUserStore().isRoleGte(UserRole.MEMBER) && space.accessibleByTeam) ||
        space.accessibleByUserDuids.includes(this.$useUserStore().duid)
      )
    ) {
      if (currentSpace) {
        this.$deleteSpace(space);
      }
      return;
    }

    // eslint-disable-next-line no-param-reassign
    space.pageKind = PageKind.SPACE;

    if (!currentSpace) {
      this._duidsToSpaces.set(space.duid, space);
    } else {
      Object.assign(currentSpace, space);
    }
    updateInSorted(this._duidsToSpaces, this._sortedSpaceDuids, space.duid, spaceComparator);

    /* Create */
    // TODO shouldn't rely on client to get the dartboards and folders separately, this should probably come over WS
    if (!currentSpace) {
      const { data: dartboardData } = await this.$backendOld.dartboards.getBySpace(space.duid);
      dartboardData.items.forEach((dartboard: Dartboard) => {
        this.$createDartboard(dartboard);
      });
      const { data: folderData } = await this.$backendOld.folders.getBySpace(space.duid);
      folderData.items.forEach((folder: Folder) => {
        this.$createOrUpdateFolder(folder);
      });
    }
  },
  $deleteSpace(space) {
    this._duidsToSpaces.delete(space.duid);
    removeFromSorted(this._sortedSpaceDuids, space.duid);

    // TODO shouldn't rely on client to delete these, this should probably come over WS; also this is bad because it can be bad for the normal delete flow
    this.getDartboardsBySpaceDuidOrdered(space.duid, { includeHidden: true }).forEach(this.$deleteDartboard);
    this.getFoldersBySpaceDuidOrdered(space.duid, { includeHidden: true }).forEach(this.$deleteFolder);
  },
};

export { actions, getters };
